import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilsService } from '../../../../services/utils/utils.service';
import { ToastrService } from 'ngx-toastr';

import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FaceDbService } from '../../../../services/face-db/face-db.service';
import { FaceCompareService } from '../../../../services/face-compare/face-compare.service';
import { SearchHistoryService } from '../../../../services/search-history/search-history.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-scanned-results',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './scanned-results.component.html',
  styleUrls: ['./scanned-results.component.css'],
})
export class ScannedResultsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  // Dual-image approach: Each face has both 'image' (clean) and 'imageWithBoxes'
  // - 'image': Original clean image for detail view, cropping, and processing
  // - 'imageWithBoxes': Pre-rendered with bounding boxes for thumbnail grid display
  extractedFaceImages: any[] = [];
  selectedFace: any;
  selectedFaceDisplayImage: string | null = null;
  croppedFaces: any = [];
  showFaces: any = false;
  zoomLevel: number = 1;
  sortBy: any = 'confidence';
  isMirroring: boolean = false;
  isProcessingImages: boolean = false;
  isStaleResult = false;
  processingProgress: { current: number; total: number } = {
    current: 0,
    total: 0,
  };

  // For panning
  isPanning = false;
  startX = 0;
  startY = 0;
  panX = 0;
  panY = 0;

  showOptions: boolean = false;

  enhance: any = {
    showReset: false,
    show: false,
    completed: false,
    started: false,
    image: '',
  };

  @ViewChild('selectedFaceCanvas')
  selectedCanvasRef!: ElementRef<HTMLCanvasElement>;

  constructor(
    private facedb: FaceDbService,
    private facecompare: FaceCompareService,
    private searchHistoryService: SearchHistoryService,
    private utilsService: UtilsService,
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit() {
    const navState = history.state || {};
    const videoScannerIdFromQuery =
      this.route.snapshot.queryParamMap.get('video_scanner_id');
    const videoScannerId =
      videoScannerIdFromQuery || navState.videoScannerId || null;
    const videoScannerHistory = navState.videoScannerHistory || null;

    if (videoScannerId) {
      this.isStaleResult = true;
      let expectedFacesFound = Number(videoScannerHistory?.faces_found) || 0;
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { video_scanner_id: videoScannerId },
        replaceUrl: true,
      });

      if (videoScannerHistory?.faces_found == null) {
        try {
          const historyResponse = await firstValueFrom(
            this.searchHistoryService.getVideoScannerHistories(
              1,
              1,
              'video_scanner_id',
              videoScannerId,
            ),
          );
          const row = historyResponse?.data?.data_list?.[0];
          if (row?.faces_found != null) {
            expectedFacesFound = Number(row.faces_found) || 0;
          }
        } catch {
          // keep default faces count
        }
      }

      await this.hydrateFromServerFrames(videoScannerId, expectedFacesFound);
    } else {
      // Live scan path: IndexedDB from the current frame-generator run
      this.extractedFaceImages = await this.facedb.getFaces();
    }

    await this.drawBoundingBoxesOnExtractedFaceImages();
    this.sortFaces(this.sortBy, 'desc');
    if (this.extractedFaceImages?.length) {
      // Defer until ViewChild canvas is available after first paint
      setTimeout(() => this.selectFrame(0), 0);
    }
  }

  /**
   * Map GET /video-scanner/frames into the shape scanned-results expects
   * ({ image, data: detect payload, timestamp }) without relying on IndexedDB.
   * API size max is 100 — paginate until all frames are loaded.
   */
  private async hydrateFromServerFrames(
    videoScannerId: string,
    expectedFacesFound = 0,
  ): Promise<void> {
    this.isProcessingImages = true;
    try {
      const pageSize = 100;
      const maxPages = 50;
      let page = 1;
      let hasNext = true;
      const allFrames: any[] = [];

      while (hasNext && page <= maxPages) {
        const response = await firstValueFrom(
          this.searchHistoryService.getVideoScannerFrames(
            videoScannerId,
            page,
            pageSize,
          ),
        );
        const frames = response?.data?.data_list || [];
        allFrames.push(...frames);
        hasNext = Boolean(response?.data?.has_next);
        page += 1;
      }

      this.extractedFaceImages = allFrames.map((frame: any) =>
        this.mapServerFrameToResult(frame),
      );

      if (!this.extractedFaceImages.length) {
        if (expectedFacesFound > 0) {
          this.toastr.warning(
            'This scan reported faces, but no stored frames were found. They may have failed to persist (storage race). Try re-running the scan.',
          );
        } else {
          this.toastr.info('No stored frames found for this scan.');
        }
      }
    } catch (error) {
      console.error(error);
      this.toastr.error('Failed to load saved scan frames');
      this.extractedFaceImages = [];
    } finally {
      this.isProcessingImages = false;
    }
  }

  private mapServerFrameToResult(frame: any): {
    image: string;
    data: any;
    timestamp: number;
  } {
    const detectPayload = this.normalizeDetectPayload(frame?.detect_payload);
    return {
      image:
        frame?.image_url || 'https://placehold.co/400x400?text=Frame',
      data: detectPayload,
      timestamp: Number(frame?.timestamp_sec) || 0,
    };
  }

  private normalizeDetectPayload(raw: any): any {
    if (!raw || typeof raw !== 'object') {
      return {
        detected_faces: [],
        image_width: 640,
        image_height: 480,
      };
    }

    // Stored as detect API `data` object, or wrapped { data: {...} }
    if (Array.isArray(raw.detected_faces) || Array.isArray(raw.faces)) {
      return {
        ...raw,
        detected_faces: raw.detected_faces || raw.faces || [],
        image_width: raw.image_width || raw.width || 640,
        image_height: raw.image_height || raw.height || 480,
      };
    }

    if (raw.data && typeof raw.data === 'object') {
      return this.normalizeDetectPayload(raw.data);
    }

    return {
      detected_faces: [],
      image_width: 640,
      image_height: 480,
      ...raw,
    };
  }

  sortFaces(criteria: string, order: 'asc' | 'desc' = 'asc') {
    if (!this.extractedFaceImages) return;

    const getAverageConfidence = (faces: any[]): number => {
      if (!faces || faces.length === 0) return 0;
      const total = faces.reduce(
        (sum, face) => sum + (face.confidence || 0),
        0,
      );
      return total / faces.length;
    };

    const compare = (a: any, b: any) => {
      let valueA: number = 0;
      let valueB: number = 0;

      switch (criteria) {
        case 'time':
          valueA = a.timestamp || 0;
          valueB = b.timestamp || 0;
          break;

        case 'faces':
          valueA = a.data?.detected_faces?.length || 0;
          valueB = b.data?.detected_faces?.length || 0;
          break;

        case 'confidence':
          valueA = this.getMaxConfidence(a.data?.detected_faces || []);
          valueB = this.getMaxConfidence(b.data?.detected_faces || []);
          break;

        default:
          return 0;
      }

      const result = valueA - valueB;
      return order === 'asc' ? result : -result;
    };

    this.extractedFaceImages.sort(compare);
  }

  getMaxConfidence = (faces: any[]): number => {
    if (!faces || faces.length === 0) return 0;
    return Math.max(...faces.map((f) => f.confidence || 0));
  };

  updateSort(event: any) {
    this.sortBy = event.target.value;
    this.sortFaces(this.sortBy, 'desc');
  }

  ngAfterViewInit() {
    const canvas = this.selectedCanvasRef?.nativeElement;
    if (!canvas) {
      return;
    }

    // --- Mouse Events ---
    canvas.addEventListener('mousedown', (event) => this.startPan(event));
    canvas.addEventListener('mousemove', (event) => this.pan(event));
    canvas.addEventListener('mouseup', () => this.endPan());
    canvas.addEventListener('mouseleave', () => this.endPan());

    // --- Touch Events ---
    canvas.addEventListener('touchstart', (event) =>
      this.startPan(event.touches[0]),
    );
    canvas.addEventListener('touchmove', (event) => this.pan(event.touches[0]));
    canvas.addEventListener('touchend', () => this.endPan());

    // --- Scroll-to-Zoom Event ---
    canvas.addEventListener('wheel', (event) => this.onMouseWheelZoom(event));
  }

  // ✅ Scroll wheel zoom
  onMouseWheelZoom(event: WheelEvent) {
    if (!this.selectedFace) return;
    event.preventDefault();

    const zoomIntensity = 0.1;
    const oldZoom = this.zoomLevel;

    // Zoom in or out
    if (event.deltaY < 0) {
      this.zoomLevel = Math.min(this.zoomLevel + zoomIntensity, 5); // max 5x zoom
    } else {
      this.zoomLevel = Math.max(this.zoomLevel - zoomIntensity, 1); // min 1x
    }

    // Optional: keep zoom centered around cursor
    const canvas = this.selectedCanvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const mouseX = event.clientX - rect.left - canvas.width / 2;
    const mouseY = event.clientY - rect.top - canvas.height / 2;

    this.panX -= (mouseX * (this.zoomLevel - oldZoom)) / this.zoomLevel;
    this.panY -= (mouseY * (this.zoomLevel - oldZoom)) / this.zoomLevel;

    this.redrawCanvas();
  }

  startPan(event: MouseEvent | Touch) {
    if (!this.selectedFace) return;
    this.isPanning = true;
    this.startX = event.clientX - this.panX;
    this.startY = event.clientY - this.panY;
  }

  pan(event: MouseEvent | Touch) {
    if (!this.isPanning || !this.selectedFace) return;

    const canvas = this.selectedCanvasRef.nativeElement;
    const zoomedWidth = canvas.width * this.zoomLevel;
    const zoomedHeight = canvas.height * this.zoomLevel;

    let newPanX = event.clientX - this.startX;
    let newPanY = event.clientY - this.startY;

    const maxOffsetX = (zoomedWidth - canvas.width) / 2;
    const maxOffsetY = (zoomedHeight - canvas.height) / 2;

    this.panX = this.clamp(newPanX, -maxOffsetX, maxOffsetX);
    this.panY = this.clamp(newPanY, -maxOffsetY, maxOffsetY);

    this.redrawCanvas();
  }

  endPan() {
    this.isPanning = false;
  }

  redrawCanvas() {
    if (!this.selectedFace) return;

    const canvas = this.selectedCanvasRef.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    // Use display image if mirrored, otherwise use original from array
    img.src = this.selectedFaceDisplayImage || this.selectedFace.image;

    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const zoomedWidth = canvas.width * this.zoomLevel;
      const zoomedHeight = canvas.height * this.zoomLevel;

      const offsetX = (canvas.width - zoomedWidth) / 2 + this.panX;
      const offsetY = (canvas.height - zoomedHeight) / 2 + this.panY;

      ctx.drawImage(img, offsetX, offsetY, zoomedWidth, zoomedHeight);

      const scaleX =
        (canvas.width * this.zoomLevel) / this.selectedFace.data.image_width;
      const scaleY =
        (canvas.height * this.zoomLevel) / this.selectedFace.data.image_height;

      this.selectedFace.data.detected_faces.forEach((detected: any) => {
        const box = detected.bbox;

        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffff44ff';
        ctx.beginPath();
        ctx.roundRect(
          box.x * scaleX + offsetX,
          box.y * scaleY + offsetY,
          box.width * scaleX,
          box.height * scaleY,
          8, // smooth corners
        );
        ctx.stroke();

        const label = `${(detected.confidence * 100).toFixed(2)}%`;
        ctx.fillStyle = 'rgba(255, 255, 0, 0.8)';
        ctx.font = '12px Arial';
        const textWidth = ctx.measureText(label).width + 6;
        ctx.fillRect(
          box.x * scaleX + offsetX,
          box.y * scaleY + offsetY - 16,
          textWidth,
          14,
        );
        ctx.fillStyle = 'black';
        ctx.fillText(
          label,
          box.x * scaleX + 3 + offsetX,
          box.y * scaleY - 5 + offsetY,
        );
      });
    };
  }

  // ✅ Called when user moves zoom slider
  onZoomChange() {
    if (!this.selectedFace) return;
    this.redrawCanvas();
  }

  private async drawBoundingBoxesOnExtractedFaceImages(): Promise<void> {
    if (!this.extractedFaceImages || this.extractedFaceImages.length === 0) {
      return;
    }

    // Start loading state
    this.isProcessingImages = true;
    this.processingProgress.total = this.extractedFaceImages.length;
    this.processingProgress.current = 0;

    // console.log(
    //   '[ScannedResults] Drawing bounding boxes on',
    //   this.extractedFaceImages.length,
    //   'images',
    // );

    // Process images in batches to avoid quota errors
    const batchSize = 50;
    const processedImages: any[] = [];

    for (let i = 0; i < this.extractedFaceImages.length; i += batchSize) {
      const batch = this.extractedFaceImages.slice(i, i + batchSize);

      // console.log(
      //   `[ScannedResults] Processing batch ${Math.floor(i / batchSize) + 1} (${
      //     i + 1
      //   }-${Math.min(i + batchSize, this.extractedFaceImages.length)})`,
      // );

      const batchResults = await Promise.all(
        batch.map(async (faceData, batchIndex) => {
          const originalIndex = i + batchIndex;
          try {
            // Skip if no detected faces
            if (
              !faceData.data?.detected_faces ||
              faceData.data.detected_faces.length === 0
            ) {
              return faceData;
            }

            // Create a new image element to load the blob URL
            const img = new Image();
            img.crossOrigin = 'anonymous';

            // Load the image
            await new Promise<void>((resolve, reject) => {
              img.onload = () => resolve();
              img.onerror = () => {
                console.error(
                  '[ScannedResults] Failed to load image for index',
                  originalIndex,
                );
                reject(new Error('Failed to load image'));
              };
              img.src = faceData.image;
            });

            // Create canvas with original image dimensions
            const canvas = document.createElement('canvas');
            canvas.width = faceData.data.image_width || img.width;
            canvas.height = faceData.data.image_height || img.height;
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              console.error(
                '[ScannedResults] Failed to get canvas context for index',
                originalIndex,
              );
              return faceData;
            }

            // Draw the original image
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // Calculate scale factor based on image dimensions
            // Use average dimension as reference (or use height which is typically larger)
            const referenceSize = 1920; // Reference size for scaling
            const imageSize = Math.max(canvas.width, canvas.height); // Use larger dimension
            const scaleFactor = Math.max(
              0.5,
              Math.min(2, imageSize / referenceSize),
            ); // Scale between 0.5x and 2x

            // Calculate scaled values with larger base sizes for thicker lines and larger text
            const lineWidth = Math.max(3, Math.round(8 * scaleFactor));
            const fontSize = Math.max(18, Math.round(28 * scaleFactor));
            const textHeight = Math.max(22, Math.round(34 * scaleFactor));
            const cornerRadius = Math.max(6, Math.round(12 * scaleFactor));
            const textPadding = Math.max(4, Math.round(8 * scaleFactor));

            // Draw bounding boxes for each detected face
            faceData.data.detected_faces.forEach((detected: any) => {
              if (!detected.bbox) return;

              const box = detected.bbox;

              // Draw bounding box rectangle
              ctx.lineWidth = lineWidth;
              ctx.strokeStyle = '#ffff44ff'; // Yellow color
              ctx.beginPath();
              ctx.roundRect(box.x, box.y, box.width, box.height, cornerRadius);
              ctx.stroke();

              // Draw confidence label background
              const label = `${(detected.confidence * 100).toFixed(2)}%`;
              ctx.fillStyle = 'rgba(255, 255, 0, 0.8)';
              ctx.font = `bold ${fontSize}px Arial`;

              // Measure text with new font size
              const textMetrics = ctx.measureText(label);
              const textWidthPadding = Math.max(
                8,
                Math.round(16 * scaleFactor),
              );
              const textWidth = textMetrics.width + textWidthPadding;

              ctx.fillRect(box.x, box.y - textHeight, textWidth, textHeight);

              // Draw confidence label text
              ctx.fillStyle = '#000000';
              ctx.fillText(label, box.x + textPadding, box.y - textPadding);
            });

            // Convert canvas to blob
            const blob = await new Promise<Blob>((resolve, reject) => {
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    resolve(blob);
                  } else {
                    reject(new Error('Failed to convert canvas to blob'));
                  }
                },
                'image/jpeg',
                1, // Quality
              );
            });

            // Create new blob URL for boxed version
            const boxedBlobUrl = URL.createObjectURL(blob);

            // ✅ KEY CHANGE: Return object with BOTH versions
            // Keep original clean image AND add boxed version
            return {
              ...faceData,
              image: faceData.image, // ✅ Keep original clean image
              imageWithBoxes: boxedBlobUrl, // ✅ Add boxed version for thumbnails
            };
          } catch (error) {
            console.error(
              '[ScannedResults] Error processing image at index',
              originalIndex,
              error,
            );
            // Return original face data if processing fails
            return faceData;
          }
        }),
      );

      processedImages.push(...batchResults);

      // Update progress
      this.processingProgress.current = Math.min(
        i + batchSize,
        this.extractedFaceImages.length,
      );

      // Small delay between batches to prevent overwhelming the browser
      if (i + batchSize < this.extractedFaceImages.length) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }
    }

    // Update extractedFaceImages with processed images
    this.extractedFaceImages = processedImages;
    // console.log(
    //   '[ScannedResults] Finished drawing bounding boxes on all images',
    // );

    // End loading state
    this.isProcessingImages = false;
  }

  ngOnDestroy(): void {
    // Clean up all blob URLs to prevent memory leaks
    this.extractedFaceImages.forEach((face) => {
      // Revoke imageWithBoxes blob URL
      if (
        face.imageWithBoxes &&
        typeof face.imageWithBoxes === 'string' &&
        face.imageWithBoxes.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(face.imageWithBoxes);
        } catch (error) {
          // Ignore errors when revoking URL
        }
      }
      // Revoke original image blob URL
      if (
        face.image &&
        typeof face.image === 'string' &&
        face.image.startsWith('blob:')
      ) {
        try {
          URL.revokeObjectURL(face.image);
        } catch (error) {
          // Ignore errors when revoking URL
        }
      }
    });

    // Clean up display image if it exists
    if (
      this.selectedFaceDisplayImage &&
      this.selectedFaceDisplayImage.startsWith('blob:')
    ) {
      try {
        URL.revokeObjectURL(this.selectedFaceDisplayImage);
      } catch (error) {
        // Ignore errors when revoking URL
      }
    }
  }

  formatTime(seconds: number): string {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins} m ${secs} s` : `${secs} s`;
  }

  extractSingleFace() {
    this.showFaces = true;
  }

  async searchWithFace(faceImage: string, event?: Event): Promise<void> {
    event?.stopPropagation();

    if (!faceImage) {
      this.toastr.error('No face image available to search');
      return;
    }

    try {
      const prefillImage = await this.convertBlobUrlToBase64(faceImage);
      this.router.navigate(['/osint-image-search'], {
        state: { imagePreview: prefillImage },
      });
    } catch {
      this.toastr.error('Failed to prepare image for search');
    }
  }

  selectFrame(index: number) {
    this.showFaces = false;
    this.selectedFace = this.extractedFaceImages[index];
    if (!this.selectedFace) {
      return;
    }
    // Reset display image to original when selecting a new face
    this.selectedFaceDisplayImage = null;

    this.isPanning = false;
    this.startX = 0;
    this.startY = 0;
    this.panX = 0;
    this.panY = 0;
    this.zoomLevel = 1;

    if (this.selectedCanvasRef?.nativeElement) {
      this.selectedCanvasRef.nativeElement.height =
        this.selectedFace.data?.image_height || 480;
      this.selectedCanvasRef.nativeElement.width =
        this.selectedFace.data?.image_width || 640;
    }

    this.getSelectedFaces();
    this.redrawCanvas();
  }

  getSelectedFaces(): Promise<string[]> {
    return new Promise((resolve) => {
      if (!this.selectedFace) return resolve([]);

      const croppedFaces: string[] = [];
      const img = new Image();
      // Use display image if mirrored, otherwise use original from array
      img.src = this.selectedFaceDisplayImage || this.selectedFace.image;

      img.onload = () => {
        const scaleX = img.width / this.selectedFace.data.image_width;
        const scaleY = img.height / this.selectedFace.data.image_height;

        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return resolve([]);

        this.selectedFace.data.detected_faces.forEach((detected: any) => {
          const box = detected.bbox;
          tempCanvas.width = box.width;
          tempCanvas.height = box.height;
          tempCtx.clearRect(0, 0, box.width, box.height);
          tempCtx.drawImage(
            img,
            box.x * scaleX,
            box.y * scaleY,
            box.width * scaleX,
            box.height * scaleY,
            0,
            0,
            box.width,
            box.height,
          );
          croppedFaces.push(tempCanvas.toDataURL('image/jpeg'));
        });

        this.croppedFaces = croppedFaces;
        resolve(croppedFaces);
      };

      img.onerror = () => resolve([]);
    });
  }

  toggleOptions() {
    this.showOptions = !this.showOptions;
  }

  // Called when user clicks "Enhance" icon
  openEnhance() {
    this.enhance.started = false;
    this.enhance.completed = false;
    this.enhance.show = true;
    this.enhance.showReset = false;
    // Use display image if mirrored, otherwise use original from array
    this.enhance.image =
      this.selectedFaceDisplayImage || this.selectedFace.image;
  }

  // Close overlay
  closeEnhance() {
    this.enhance.started = false;
    this.enhance.completed = false;
    this.enhance.showReset = false;
    this.enhance.show = false;
  }

  // Enhance or Apply logic
  async onEnhanceClick() {
    this.enhance.started = true;

    try {
      // Convert blob URL to base64 before sending to API
      const imageUrl = this.selectedFaceDisplayImage || this.selectedFace.image;
      const imageBase64 = await this.convertBlobUrlToBase64(imageUrl);

      this.facecompare.getEnhancedImage(imageBase64).subscribe({
        next: (data) => {
          let imageBase64 = data.data;
          this.enhance.image = 'data:image/jpeg;base64,' + imageBase64;
          this.enhance.completed = true;
          this.enhance.showReset = true;
        },
        error: (error) => {
          console.error('Enhancement failed:', error);
          this.enhance.started = false;
          this.toastr.error('Failed to enhance image. Please try again.');
        },
      });
    } catch (error) {
      console.error('Failed to convert image:', error);
      this.enhance.started = false;
      this.toastr.error('Failed to process image. Please try again.');
    }
  }

  // Reset logic
  resetEnhance() {
    // Use display image if mirrored, otherwise use original from array
    this.enhance.image =
      this.selectedFaceDisplayImage || this.selectedFace.image;
    this.enhance.started = false;
    this.enhance.completed = false;
    this.enhance.showReset = false;
  }

  applyEnhanceResult() {
    // Update display image (doesn't affect array)
    this.selectedFaceDisplayImage = this.enhance.image;
    this.enhance.show = false;
    this.isPanning = false;
    this.startX = 0;
    this.startY = 0;
    this.panX = 0;
    this.panY = 0;
    this.zoomLevel = 1;
    this.getSelectedFaces();
    this.redrawCanvas();
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  /**
   * Converts a blob URL or data URL to base64 string
   * @param imageUrl - Blob URL (blob:...) or data URL (data:image/...)
   * @returns Base64 string with data URL prefix (data:image/jpeg;base64,...)
   */
  private async convertBlobUrlToBase64(imageUrl: string): Promise<string> {
    // If already a data URL (base64), return as is
    if (imageUrl.startsWith('data:')) {
      return imageUrl;
    }

    // If it's a blob URL, fetch and convert to base64
    if (imageUrl.startsWith('blob:')) {
      try {
        // Fetch the blob
        const response = await fetch(imageUrl);
        const blob = await response.blob();

        // Convert blob to base64 using FileReader
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64String = reader.result as string;
            resolve(base64String);
          };
          reader.onerror = () => {
            reject(new Error('Failed to read blob as base64'));
          };
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error converting blob URL to base64:', error);
        throw error;
      }
    }

    // If it's neither blob nor data URL, return as is (shouldn't happen)
    console.warn('Unexpected image URL format:', imageUrl);
    return imageUrl;
  }

  async mirrorImage() {
    if (!this.selectedFace?.image) return;

    try {
      this.isMirroring = true;
      // ✅ Use the faster backend mirroring service
      // Use original image from array, not the display image
      const imageToMirror =
        this.selectedFaceDisplayImage || this.selectedFace.image;
      const mirroredUrl = await this.utilsService.createMirroredImage(
        imageToMirror,
      );

      // Store mirrored image in separate display property (doesn't affect array)
      this.selectedFaceDisplayImage = mirroredUrl;

      // ✅ Convert mirrored image to base64 before face detection
      const mirroredBase64 = await this.convertBlobUrlToBase64(
        this.selectedFaceDisplayImage,
      );

      // ✅ Re-run face detection just like before
      this.facecompare.getFacesFromImage(mirroredBase64).subscribe({
        next: (data) => {
          // Create a copy of data to avoid modifying the original
          this.selectedFace = { ...this.selectedFace, data: data.data };

          // Reset UI states (same as your old function)
          this.isPanning = false;
          this.startX = 0;
          this.startY = 0;
          this.panX = 0;
          this.panY = 0;
          this.zoomLevel = 1;

          // Refresh faces and redraw canvas
          this.getSelectedFaces();
          this.redrawCanvas();
          this.isMirroring = false;
        },
        error: (error) => {
          console.error('Face detection failed on mirrored image:', error);
          this.toastr.error('Failed to detect faces on mirrored image');
          this.isMirroring = false;
        },
      });
    } catch (error) {
      console.error('Mirror image failed:', error);
      if (this.toastr) this.toastr.error('Failed to mirror image');
      this.isMirroring = false;
    }
  }

  mirrorBase64Image(base64: string, horizontal = true): any {
    const img = new Image();
    img.src = base64;
    return new Promise<string>((resolve) => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        canvas.width = img.width;
        canvas.height = img.height;
        ctx.save();
        if (horizontal) {
          ctx.scale(-1, 1);
          ctx.drawImage(img, -img.width, 0);
        } else {
          ctx.scale(1, -1);
          ctx.drawImage(img, 0, -img.height);
        }
        ctx.restore();
        resolve(canvas.toDataURL());
      };
    });
  }
}
